/**
 * The «Approval rules» tab (masterplan credit-limits-v3, B5; E3, E4, E16, E18, E30, E34). Everyone with the page
 * right reads the rules; only Finance edits (`roles.finance`, and only once migration 227 made the tables). A person
 * who is not Finance gets no input, no select and no save button at all, not disabled ones.
 *
 * Four blocks: the amount tiers (the roles per tier are fixed), the approvers per sales manager, the Finance people
 * (an on/off switch, the last active one stays) and the thresholds (overdue days, «near the limit» percentage, the
 * default top level for customers without a seller). Every save sends only what changed and shows the API's answer;
 * the API writes one config-log row per changed row. Open requests keep the approvers they were created with (E18).
 */
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import type {
    ApproverUpdateBody, CreditLimitApprover, CreditLimitConfig, CreditLimitConfigWriteResult,
} from '../../../types/creditLimits';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import { apiErrorText } from '../requests/requestRules';
import { nameFromEmail } from '../requests/chainPreview';
import { ApproverDialog } from './ApproverDialog';
import { NavLockBlock } from './NavLockBlock';
import {
    REQUIRED_ROLES_TEXT, canEditRules, checkSettingsDraft, checkTiersDraft, formatChfPlain, freeSalesManagers,
    isLastActiveFinance, personOptions, settingsBody, settingsDraftOf, tierRangeText, tiersBody, tiersDraftOf,
    type SettingsDraft, type TiersDraft,
} from './ruleForm';
import { useCreditLimitConfig } from './useCreditLimitConfig';

interface Props {
    /** Called after a save, so the page can refresh what depends on the rules. */
    onChanged?: () => void;
}

const WRAP_STYLE = { '--tbl-h': 'none' } as CSSProperties;
const H3_STYLE: CSSProperties = { margin: '0 0 8px', fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)', fontWeight: 640, color: 'var(--text-strong)' };

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
    return (
        <section style={{ marginBottom: 28 }}>
            <h3 style={H3_STYLE}>{title}</h3>
            {hint && <p className="muted" style={{ margin: '0 0 8px' }}>{hint}</p>}
            {children}
        </section>
    );
}

function who(email: string | null | undefined) {
    if (!email) return <span className="muted">–</span>;
    return <span title={email}>{nameFromEmail(email)}</span>;
}

export function RulesTab({ onChanged }: Props) {
    const { config, loading, error, reload, save } = useCreditLimitConfig();
    const [notice, setNotice] = useState<string | null>(null);
    const [dialog, setDialog] = useState<{ row: CreditLimitApprover | null } | null>(null);
    const editable = canEditRules(config);

    // One save for every block: runs the call, reports "n changes saved", lets the page refresh, rethrows for the form.
    const run = async (what: string, write: () => Promise<CreditLimitConfigWriteResult>) => {
        setNotice(null);
        const result = await save(write);
        setNotice(result.changed === 0 ? `${what}: nothing changed.` : `${what}: saved (${result.changed} ${result.changed === 1 ? 'change' : 'changes'}).`);
        if (result.changed > 0) onChanged?.();
        return result;
    };

    return (
        <div className="clim-panel" style={{ '--tbl-offset': '300px' } as CSSProperties}>
            {error && (
                <div className="banner error" role="alert">
                    <p>{error}</p>
                    <div className="actions"><button type="button" className="btn secondary sm" onClick={reload}>Retry</button></div>
                </div>
            )}
            {loading && !config && <p className="muted" role="status">Loading the approval rules…</p>}
            {config && (
                <>
                    <div className="banner info" role="note" style={{ marginBottom: 16 }}>
                        <p>
                            Open requests keep the approvers they were created with. A change here applies to requests created
                            afterwards; if a person on an open request is gone, Finance approves on behalf.
                            {editable ? '' : ' Only Finance can change the rules.'}
                        </p>
                    </div>
                    {!config.schemaReady && (
                        <div className="banner attention" role="status" style={{ marginBottom: 16 }}>
                            <p>The approval rules are not set up yet (migration 227). The defaults are shown and cannot be changed.</p>
                        </div>
                    )}
                    {notice && <p className="muted" role="status" style={{ margin: '0 0 12px' }}>{notice}</p>}

                    <NavLockBlock
                        config={config}
                        editable={editable}
                        onUnlock={async (confirmPasswordUpdated = false) => {
                            setNotice(null);
                            await save(() => creditLimitsApi.navUnlock(confirmPasswordUpdated));
                            setNotice('NAV writing is unlocked.');
                            onChanged?.();
                        }}
                    />

                    <TiersBlock config={config} editable={editable} onSave={body => run('Tiers', () => creditLimitsApi.putTiers(body))} />

                    <Section title="Approvers per sales manager" hint="Who approves the customers of each sales manager. The top level is Bruno Crippa for everybody unless changed here.">
                        <div className="tbl-wrap" style={WRAP_STYLE}>
                            <table className="tbl">
                                <caption className="sr-only">Approvers per sales manager</caption>
                                <colgroup>
                                    <col style={{ width: 220 }} /><col style={{ width: 200 }} /><col style={{ width: 200 }} />
                                    <col style={{ width: 200 }} /><col style={{ width: 90 }} />{editable && <col style={{ width: 90 }} />}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th scope="col">Sales manager</th><th scope="col">Salesperson</th><th scope="col">BT manager</th>
                                        <th scope="col">Top level</th><th scope="col">Status</th>
                                        {editable && <th scope="col"><span className="sr-only">Edit</span></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {config.approvers.length === 0 && (
                                        <tr className="empty-row"><td colSpan={editable ? 6 : 5}>No approver rows yet.</td></tr>
                                    )}
                                    {config.approvers.map(a => (
                                        <tr key={a.id}>
                                            <td className="key">{a.salesManagerName}</td>
                                            <td>{who(a.smEmail)}</td>
                                            <td>{who(a.btmEmail)}</td>
                                            <td>{who(a.topEmail)}</td>
                                            <td className={a.isActive ? undefined : 'dash'}>{a.isActive ? 'Active' : 'Off'}</td>
                                            {editable && (
                                                <td>
                                                    <button type="button" className="btn secondary sm" onClick={() => setDialog({ row: a })}>
                                                        Edit<span className="sr-only">{` ${a.salesManagerName}`}</span>
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {editable && (
                            <p style={{ margin: '8px 0 0' }}>
                                <button
                                    type="button"
                                    className="btn secondary sm"
                                    onClick={() => setDialog({ row: null })}
                                    disabled={freeSalesManagers(config.salesManagerOptions, config.approvers).length === 0}
                                >
                                    Add sales manager
                                </button>
                            </p>
                        )}
                    </Section>

                    <FinanceBlock config={config} editable={editable} onSave={body => run('Finance', () => creditLimitsApi.putFinance(body))} />

                    <SettingsBlock config={config} editable={editable} onSave={body => run('Thresholds', () => creditLimitsApi.putSettings(body))} />

                    {dialog && (
                        <ApproverDialog
                            config={config}
                            row={dialog.row}
                            onClose={() => setDialog(null)}
                            onSave={async (body: ApproverUpdateBody) => { await run('Approvers', () => creditLimitsApi.putApprover(body)); }}
                        />
                    )}
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------- tiers

function TiersBlock({ config, editable, onSave }: {
    config: CreditLimitConfig;
    editable: boolean;
    onSave: (body: NonNullable<ReturnType<typeof tiersBody>>) => Promise<unknown>;
}) {
    const saved = tiersDraftOf(config.tiers);
    const [draft, setDraft] = useState<TiersDraft>(saved);
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const uid = useId();
    const savedKey = `${saved.tier1}|${saved.tier2}`;
    // The server's numbers replace the draft after a save or a reload.
    useEffect(() => { setDraft(tiersDraftOf(config.tiers)); setTouched(false); }, [savedKey]);
    const check = checkTiersDraft(draft);
    const body = tiersBody(config.tiers, draft);

    const submit = async () => {
        setTouched(true);
        if (!body) return;
        setBusy(true);
        setError(null);
        try { await onSave(body); } catch (err) { setError(apiErrorText(err, 'The tiers could not be saved.')); } finally { setBusy(false); }
    };

    const tierRows = [...config.tiers].sort((a, b) => a.tierNo - b.tierNo);
    return (
        <Section title="Approval tiers" hint="The tier follows the amount of the NEW limit, also when a limit is lowered; the limit itself belongs to the lower tier. The approvers per tier are fixed.">
            {error && <div className="banner error" role="alert" style={{ marginBottom: 8 }}><p>{error}</p></div>}
            <div className="tbl-wrap" style={WRAP_STYLE}>
                <table className="tbl">
                    <caption className="sr-only">Approval tiers</caption>
                    <colgroup><col style={{ width: 80 }} /><col style={{ width: 260 }} /><col style={{ width: 280 }} /></colgroup>
                    <thead><tr><th scope="col">Tier</th><th scope="col">New limit (CHF)</th><th scope="col">Approval by</th></tr></thead>
                    <tbody>
                        {tierRows.map(t => (
                            <tr key={t.tierNo}>
                                <td className="key">{t.tierNo}</td>
                                <td className={editable && t.maxAmountChf !== null ? 'editing' : undefined}>
                                    {editable && t.tierNo <= 2 ? (
                                        <>
                                            <label htmlFor={`${uid}-t${t.tierNo}`} className="sr-only">{`Tier ${t.tierNo}: up to (CHF)`}</label>
                                            <input
                                                id={`${uid}-t${t.tierNo}`}
                                                className="input n sm"
                                                inputMode="decimal"
                                                value={t.tierNo === 1 ? draft.tier1 : draft.tier2}
                                                aria-invalid={touched && (t.tierNo === 1 ? check.tier1 : check.tier2) !== null}
                                                onChange={e => setDraft(d => (t.tierNo === 1 ? { ...d, tier1: e.target.value } : { ...d, tier2: e.target.value }))}
                                            />
                                        </>
                                    ) : tierRangeText(config.tiers, t.tierNo)}
                                </td>
                                <td>{REQUIRED_ROLES_TEXT[t.requiredRoles]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editable && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn primary sm" onClick={submit} disabled={busy || (check.ok && !body)}>{busy ? 'Saving…' : 'Save tiers'}</button>
                    {touched && (check.tier1 || check.tier2) && <span className="hint" role="alert">{check.tier1 ?? check.tier2}</span>}
                    {check.ok && check.amounts && (
                        <span className="muted">{`Salesperson up to CHF ${formatChfPlain(check.amounts.tier1)}, BT manager up to CHF ${formatChfPlain(check.amounts.tier2)}, above that BT manager and top level.`}</span>
                    )}
                </div>
            )}
        </Section>
    );
}

// ---------------------------------------------------------------- finance

function FinanceBlock({ config, editable, onSave }: {
    config: CreditLimitConfig;
    editable: boolean;
    onSave: (body: { email: string; isActive: boolean }) => Promise<unknown>;
}) {
    const [error, setError] = useState<string | null>(null);
    const [busyEmail, setBusyEmail] = useState<string | null>(null);

    const toggle = async (email: string, isActive: boolean) => {
        setBusyEmail(email);
        setError(null);
        try { await onSave({ email, isActive }); } catch (err) { setError(apiErrorText(err, 'The Finance person could not be changed.')); } finally { setBusyEmail(null); }
    };

    return (
        <Section title="Finance" hint="Finance people maintain these rules, approve on behalf and release requests for NAV. At least one stays active.">
            {error && <div className="banner error" role="alert" style={{ marginBottom: 8 }}><p>{error}</p></div>}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                {config.finance.length === 0 && <li className="muted">No Finance person yet.</li>}
                {config.finance.map(f => {
                    const last = f.isActive && isLastActiveFinance(config.finance, f.email);
                    return (
                        <li key={f.email} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span title={f.email} style={{ minWidth: 180 }}>{nameFromEmail(f.email)}</span>
                            {editable ? (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} title={last ? 'The last active Finance person cannot be switched off.' : undefined}>
                                    <input
                                        type="checkbox"
                                        className="check"
                                        checked={f.isActive}
                                        disabled={last || busyEmail === f.email}
                                        onChange={e => toggle(f.email, e.target.checked)}
                                    />
                                    <span>Active</span>
                                </label>
                            ) : (
                                <span className={f.isActive ? undefined : 'muted'}>{f.isActive ? 'Active' : 'Off'}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </Section>
    );
}

// ---------------------------------------------------------------- settings

const SETTING_FIELDS: { key: 'overdueRedDays' | 'overdueAmberDays' | 'nearLimitPct'; label: string; unit: string; hint: string }[] = [
    { key: 'overdueRedDays', label: 'Overdue: red after', unit: 'days', hint: 'The oldest open item is more than this many days past due.' },
    { key: 'overdueAmberDays', label: 'Overdue: amber from', unit: 'days', hint: 'From this many days past due up to the red threshold.' },
    { key: 'nearLimitPct', label: 'Near the limit from', unit: '%', hint: 'Utilization from this value up to 100 % is amber, above 100 % red.' },
];

function SettingsBlock({ config, editable, onSave }: {
    config: CreditLimitConfig;
    editable: boolean;
    onSave: (body: NonNullable<ReturnType<typeof settingsBody>>) => Promise<unknown>;
}) {
    const saved = settingsDraftOf(config.settings);
    const [draft, setDraft] = useState<SettingsDraft>(saved);
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const uid = useId();
    const savedKey = JSON.stringify(saved);
    useEffect(() => { setDraft(settingsDraftOf(config.settings)); setTouched(false); }, [savedKey]);
    const check = checkSettingsDraft(draft);
    const body = settingsBody(config.settings, draft);
    const topOptions = personOptions(config.people, config.names, config.settings.defaultTopEmail ?? null);

    const submit = async () => {
        setTouched(true);
        if (!body) return;
        setBusy(true);
        setError(null);
        try { await onSave(body); } catch (err) { setError(apiErrorText(err, 'The thresholds could not be saved.')); } finally { setBusy(false); }
    };

    const s = config.settings;
    return (
        <Section title="Thresholds and default top level">
            {error && <div className="banner error" role="alert" style={{ marginBottom: 8 }}><p>{error}</p></div>}
            <div style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
                {SETTING_FIELDS.map(f => (
                    <div key={f.key} style={{ display: 'grid', gap: 4 }}>
                        {editable ? (
                            <>
                                <label htmlFor={`${uid}-${f.key}`}>{f.label}</label>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        id={`${uid}-${f.key}`}
                                        className="input n"
                                        style={{ width: 96 }}
                                        inputMode="numeric"
                                        value={draft[f.key]}
                                        aria-invalid={touched && !!check.errors[f.key]}
                                        onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                                    />
                                    <span className="muted">{f.unit}</span>
                                </span>
                                {touched && check.errors[f.key] && <span className="hint" role="alert">{check.errors[f.key]}</span>}
                            </>
                        ) : (
                            <span><b>{f.label}:</b> {s[f.key]} {f.unit}</span>
                        )}
                        <span className="muted">{f.hint}</span>
                    </div>
                ))}
                <div style={{ display: 'grid', gap: 4 }}>
                    {editable ? (
                        <>
                            <label htmlFor={`${uid}-top`}>Top level for customers without a seller</label>
                            <select
                                id={`${uid}-top`}
                                className="input"
                                value={draft.defaultTopEmail}
                                aria-invalid={touched && !!check.errors.defaultTopEmail}
                                onChange={e => setDraft(d => ({ ...d, defaultTopEmail: e.target.value }))}
                            >
                                {topOptions.map(o => <option key={o.email} value={o.email}>{o.label}</option>)}
                            </select>
                            {touched && check.errors.defaultTopEmail && <span className="hint" role="alert">{check.errors.defaultTopEmail}</span>}
                        </>
                    ) : (
                        <span><b>Top level for customers without a seller:</b> {nameFromEmail(s.defaultTopEmail)}</span>
                    )}
                    <span className="muted">Used when a customer has no active sales manager row and the tier needs the top level.</span>
                </div>
                {editable && (
                    <div>
                        <button type="button" className="btn primary sm" onClick={submit} disabled={busy || (check.ok && !body)}>{busy ? 'Saving…' : 'Save thresholds'}</button>
                    </div>
                )}
            </div>
        </Section>
    );
}
